import React, { useState, useEffect } from 'react';

// Components
import Sidebar from '../../components/Sidebar';
import Main from './PaymentVoucher/Main';
import Form from './PaymentVoucher/Form';

import {
  getDataWithComp,
  getDocStatusColour,
  getAlert,
  getMaxPayNo,
  getClearLocalStorage,
  updateData,
  getData,
} from '../../../utils/SamuiUtils';

import { formatThaiDateUiToDate, getCreateDateTime } from '../../../utils/DateUtils';

import { getReport } from '../../../utils/ReportUtils';

import { Box } from "@mui/material";

const resolveStatusCode = (status) => {
  if (!status || typeof status !== 'object') {
    return undefined;
  }

  const valueKeys = ['DocSetStatus', 'DocSet_Status', 'DocSetstatus', 'Doc_Status', 'DocStatus'];

  for (const key of valueKeys) {
    if (status[key] !== undefined && status[key] !== null) {
      const numeric = Number(status[key]);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
  }

  if (status.DocSetStaus_Value !== undefined && status.DocSetStaus_Value !== null) {
    const numeric = Number(status.DocSetStaus_Value);
    if (!Number.isNaN(numeric)) {
      return numeric;
    }
  }

  return undefined;
};

const getStatusProp = (status, keys) => {
  for (const key of keys) {
    if (status[key] !== undefined && status[key] !== null) {
      return status[key];
    }
  }
  return undefined;
};

const normalizeStatus = (status) => {
  if (!status) {
    return null;
  }

  const label = getStatusProp(status, [
    'DocSetStaus_Name',
    'DocSetStatus_Name',
    'Doc_Status_Name',
    'DocStatus_Name',
    'DocSetStausName',
    'DocStatusName',
  ]);

  const value = resolveStatusCode(status);

  if (!label || value === undefined) {
    return null;
  }

  const color = getStatusProp(status, [
    'DocSetColour',
    'DocSetStatus_Colour',
    'DocStatus_Colour',
    'Doc_Status_Colour',
    'DocSetStaus_Colour',
  ]) || '#6c757d';

  return { label, value, color };
};

function PaymentVoucher() {
  const [mode, setMode] = useState('');
  const [dataMasterList, setDataMasterList] = useState([]);
  const [dataDetailList, setDataDetailList] = useState([]);
  const [statusColours, setStatusColours] = useState([]);
  const [maxPayNo, setMaxPayNo] = useState();
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusCodeLookup, setStatusCodeLookup] = useState({});

  useEffect(() => {
    initialize();
  }, []);

  const fetchRealtime = async () => {
    try {
      const masterList = await getDataWithComp('API_0401_PAY_H', 'ORDER BY Pay_No DESC');
      // const detailList = await getDataWithComp('API_0402_PAY_D', '');
      const payStatusColour = await getDocStatusColour('PAYPO', 'Pay_Status');

      if (masterList && masterList.length > 0) {
        const sortedData = masterList.sort((a, b) => a.Pay_No - b.Pay_No);
        setDataMasterList(sortedData);
      }

      // if (detailList && detailList.length > 0) {
      //   setDataDetailList(detailList);
      // }

      const allowedStatusDefinitions = [
        { label: 'รออนุมัติ', fallbackValue: 1, fallbackColor: '#EAA300' },
        { label: 'อนุมัติจ่าย', fallbackValue: 2, fallbackColor: '#13A10E' },
        { label: 'ยกเลิก', fallbackValue: 13, fallbackColor: '#808080' },
      ];

      if (Array.isArray(payStatusColour) && payStatusColour.length > 0) {
        setStatusColours(payStatusColour);

        const normalizedStatuses = payStatusColour
          .map(normalizeStatus)
          .filter(Boolean);

        const statusMap = new Map(
          normalizedStatuses.map((status) => [status.label, status])
        );

        const approvalStatuses = allowedStatusDefinitions.map(
          ({ label, fallbackValue, fallbackColor }) => {
            const match = statusMap.get(label);
            return {
              label,
              value: match?.value ?? fallbackValue,
              color: match?.color ?? fallbackColor,
            };
          }
        );

        setStatusOptions(approvalStatuses);
        setStatusCodeLookup(
          approvalStatuses.reduce((acc, status) => {
            acc[status.label] = status.value;
            return acc;
          }, {})
        );
      } else {
        const fallbackStatuses = allowedStatusDefinitions.map(
          ({ label, fallbackValue, fallbackColor }) => ({
            label,
            value: fallbackValue,
            color: fallbackColor,
          })
        );
        setStatusColours([]);
        setStatusOptions(fallbackStatuses);
        setStatusCodeLookup(
          fallbackStatuses.reduce((acc, status) => {
            acc[status.label] = status.value;
            return acc;
          }, {})
        );
      }

      // หาค่าสูงของ PayNo ใน PAY_H
      const findMaxPayNo = await getDataWithComp('PAY_H', 'ORDER BY Pay_No DESC');
      const maxPay = getMaxPayNo(findMaxPayNo);
      setMaxPayNo(maxPay);
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const initialize = async () => {
    try {
      await getClearLocalStorage();
      setMode('S');
      fetchRealtime();
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const onPageInsert = () => {
    setMode('I')
  };

  const onRowSelected = (payNo) => {
    setMaxPayNo(payNo);
    setMode('U');
  };

  const handleStatusChange = async (payNo, nextStatusValue, rowData) => {
    if (!payNo || nextStatusValue === undefined || nextStatusValue === null) {
      return;
    }

    const numericStatus = Number(nextStatusValue);
    if (Number.isNaN(numericStatus)) {
      return;
    }

    const currentStatus = rowData?.Pay_Status ?? rowData?.payStatus;
    if (currentStatus !== undefined && String(currentStatus) === String(numericStatus)) {
      return;
    }

    const targetOption = statusOptions.find(
      (option) => String(option.value) === String(numericStatus)
    );

    if (!targetOption) {
      getAlert('FAILED', 'ไม่พบข้อมูลสถานะที่ต้องการอัปเดต');
      return;
    }

    try {
      const now = getCreateDateTime();
      const payload = {
        Pay_Status: numericStatus,
        Update_Date: formatThaiDateUiToDate(now),
        Update_By_Name: window.localStorage.getItem('name'),
        Update_By_Id: window.localStorage.getItem('emp_id'),
      };

      await updateData('PAY_H', payload, {
        Pay_No: payNo,
        Comp_Id: window.localStorage.getItem('company'),
      });

      // หากเป็นเอกสารจ่ายตามใบสั่งซื้อ (ล่วงหน้า) ให้อัปเดตสถานะการจ่ายที่ PO_H ด้วย
      // Mapping: รอจ่าย -> 2, จ่ายแล้ว -> 3, ยกเลิก -> 1
      const isPrepayOnPO = String(rowData?.Pay_Type) === '4';
      if (isPrepayOnPO) {
        const label = targetOption.label;
        // Mapping from payment-voucher status label to PO_H.Doc_Status_Paid
        // Updated to reflect new status labels: 'รออนุมัติ' and 'อนุมัติจ่าย'
        const mappedDocStatusPaid = label === 'อนุมัติจ่าย' ? 3 : label === 'รออนุมัติ' ? 2 : 1;

        try {
          const payId = rowData?.Pay_Id;
          if (payId) {
            // ดึงรายละเอียดของใบสำคัญจ่าย เพื่อหาเอกสาร PO ที่เกี่ยวข้อง
            const details = await getData('PAY_D', `Pay_Id = ${payId}`);
            if (Array.isArray(details) && details.length > 0) {
              const uniquePoDocIds = Array.from(
                new Set(details.map((d) => d?.Rec_Id).filter(Boolean))
              );

              // อัปเดตสถานะการจ่ายใน PO_H สำหรับทุกใบที่เกี่ยวข้อง
              for (const poDocId of uniquePoDocIds) {
                await updateData(
                  'PO_H',
                  { Doc_Status_Paid: mappedDocStatusPaid },
                  {
                    Doc_Id: poDocId,
                    Comp_Id: window.localStorage.getItem('company'),
                  }
                );
              }
            }
          }
        } catch (poErr) {
          // ไม่ให้ล้มเหลวทั้งกระบวนการ แต่แจ้งเตือนผู้ใช้
          getAlert('FAILED', poErr?.response?.data?.message || poErr.message || 'อัปเดตสถานะที่ PO ไม่สำเร็จ');
        }
      }

      await fetchRealtime();
    } catch (error) {
      getAlert('FAILED', error?.response?.data?.message || error.message);
    }
  };

  const isStatusEditable = () => true;

  return (
    <Box className="PaymentVoucher" sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, ml: 'var(--sidebar-width, 220px)', p: 3 }}>
        {mode === 'S' ? (
          <Main
            masterList={dataMasterList}
            detailList={dataDetailList}
            statusColours={statusColours}
            name={'ใบสำคัญจ่าย'}
            onPageInsert={onPageInsert}
            onRowSelected={onRowSelected}
            getReport={getReport}
            statusOptions={statusOptions}
            onStatusChange={handleStatusChange}
            isStatusEditable={isStatusEditable}
          />
        ) : (
          <Form
            callInitialize={initialize}
            mode={mode}
            setMode={setMode}
            name={'ใบสำคัญจ่าย'}
            maxPayNo={maxPayNo}
            getReport={getReport}
          />
        )}
      </Box>
    </Box>
  );
}

export default PaymentVoucher;
